import React, { useRef } from 'react';
import { Check } from 'lucide-react';
import WhatsAppCTA from './WhatsAppCTA';
import { CTA_LABEL, packages } from '../data/site';
import { useGSAP, gsap, MOTION_OK } from '../lib/gsap';
import { cn } from '../lib/utils';

const packageMessage = (name) =>
  `Olá, Paulo! Vim pelo site e tenho interesse no pacote ${name}.\n\nTipo de evento: \nData: \nLocal/cidade: \nNº de convidados: `;

const Price = ({ value, large }) =>
  value ? (
    <p className="mt-6">
      <span className="block text-sm text-soft">a partir de</span>
      <span className={cn('font-display font-bold tracking-tight', large ? 'text-5xl' : 'text-3xl')}>{value}</span>
    </p>
  ) : (
    <p className={cn('mt-6 font-display font-bold tracking-tight', large ? 'text-3xl' : 'text-2xl')}>Sob consulta</p>
  );

const Packages = () => {
  const scope = useRef(null);
  const [featured, ...others] = packages;

  // Os pacotes secundários entram primeiro e o recomendado chega por último,
  // levemente maior: a comparação ancora a escolha no Completo.
  useGSAP(
    () => {
      gsap.matchMedia().add(MOTION_OK, () => {
        gsap
          .timeline({ scrollTrigger: { trigger: scope.current, start: 'top 70%', once: true } })
          .from('[data-pkg-head]', { autoAlpha: 0, y: 24 })
          .from('[data-pkg-other]', { autoAlpha: 0, x: 24, stagger: 0.1 }, '-=0.4')
          .from('[data-pkg-featured]', { autoAlpha: 0, scale: 0.96, y: 24, duration: 0.9 }, '-=0.3');
      });
    },
    { scope }
  );

  return (
    <section id="pacotes" ref={scope} data-testid="packages-section" className="bg-raised/80 px-4 py-20 md:px-8 md:py-28">
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 max-w-2xl md:mb-14" data-pkg-head>
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-soft">Pacotes</p>
          <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl">Escolha o tamanho da festa.</h2>
          <p className="mt-4 text-lg text-soft">O valor final depende de data, local e duração.</p>
        </div>

        <div className="grid gap-4 lg:grid-cols-12">
          <article
            data-pkg-featured
            data-testid={`package-${featured.id}`}
            className="flex flex-col rounded-2xl border-2 border-ink/25 bg-page p-7 md:p-10 lg:col-span-7"
          >
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{featured.name}</h3>
              <span className="rounded-full bg-ink px-3 py-1 text-xs font-semibold text-page">Recomendado</span>
            </div>
            <p className="mt-2 text-lg text-soft">{featured.fit}</p>
            <Price value={featured.price} large />
            <ul className="mb-9 mt-8 grid content-start gap-x-6 gap-y-3 sm:grid-cols-2">
              {featured.items.map((item) => (
                <li key={item} className="flex gap-3">
                  <Check size={20} strokeWidth={2} className="mt-0.5 shrink-0 text-ink" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <WhatsAppCTA message={packageMessage(featured.name)} source={`package_${featured.id}`} className="mt-auto self-start">
              {CTA_LABEL}
            </WhatsAppCTA>
          </article>

          <div className="grid gap-4 lg:col-span-5">
            {others.map((pkg) => (
              <article key={pkg.id} data-pkg-other data-testid={`package-${pkg.id}`} className="flex flex-col rounded-2xl border border-line bg-page p-7">
                <h3 className="font-display text-2xl font-bold tracking-tight">{pkg.name}</h3>
                <p className="mt-1 text-soft">{pkg.fit}</p>
                <Price value={pkg.price} />
                <ul className="mb-6 mt-4 space-y-2 text-sm">
                  {pkg.items.map((item) => (
                    <li key={item} className="flex gap-2.5">
                      <Check size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-soft" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
                <WhatsAppCTA message={packageMessage(pkg.name)} source={`package_${pkg.id}`} variant="outline" size="sm" className="mt-auto self-start">
                  {CTA_LABEL}
                </WhatsAppCTA>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Packages;
